var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { Component } from 'react';
import { CalendarList, LocaleConfig } from './component';
import { withTheme } from '../../theme';
import PropTypes from 'prop-types';
import R from '../../core-res/R';
import DRNDateCell from '../DateCell';
import { ScrollView, View } from 'react-native';
import { DRNUtil, DRNDateUtil } from '@traveloka/district-util';
import { TOTAL_HEIGHT } from '../DateCell/index';
import locales from './locales';
import VARIANTS from './variants';
import DAY_CONST from './const';
import { getSelectedDatesObj, getConvertedHolidayObj, getFormattedDate } from './util';
import DRNHolidayList from '../HolidayList';
import merge from 'lodash/merge';
const HEADER_HEIGHT = R.typography.fontSize.medium + 20 + R.typography.fontSize.small + 20 + 7;
class Calendar extends Component {
    constructor(props) {
        super(props);
        this.state = {
            startingDay: null,
            endingDay: null,
        };
        this.isRangeSelected = () => {
            const { startingDay, endingDay } = this.state;
            return startingDay && endingDay && startingDay !== endingDay;
        };
        this.getRangeObj = () => {
            const { startingDay, endingDay } = this.state;
            return {
                startingDay,
                endingDay,
            };
        };
        this.getStartingDayDateObj = () => {
            return new Date(this.state.startingDay);
        };
        this.getEndingDayDateObj = () => {
            if (!this.state.endingDay)
                return null;
            return new Date(this.state.endingDay);
        };
        this.getContainerStyle = () => {
            if (!DRNUtil.isWeb())
                return;
            return {
                flex: 1,
                height: 0,
                width: '100%',
            };
        };
        this.handleDatePress = day => {
            const { variant } = this.props;
            this.props.onDayPress && this.props.onDayPress(day);
            if (variant === VARIANTS.SINGLE_SELECT) {
                this.setStartingDay(day);
                return;
            }
            const { startingDay, endingDay } = this.state;
            const date = new Date(day);
            const startDate = this.getStartingDayDateObj();
            const endDate = this.getEndingDayDateObj();
            if (!startingDay && !endingDay) {
                this.setStartingDay(day);
            }
            else {
                if (DRNDateUtil.isEqual(date, endDate) || DRNDateUtil.isEqual(date, startDate)) {
                    this.setEndingDay(null);
                    this.setStartingDay(day);
                }
                else if (DRNDateUtil.isBefore(date, startDate)) {
                    this.setStartingDay(day);
                }
                else if (DRNDateUtil.isAfter(date, startDate)) {
                    this.setEndingDay(day);
                }
            }
        };
        this.setStartingDay = startingDay => {
            this.setState({
                startingDay,
            });
        };
        this.setEndingDay = endingDay => {
            this.setState({
                endingDay,
            });
        };
        this.getMarkedDatesObj = () => {
            if (!this.state.startingDay)
                return {};
            const { theme } = this.props;
            const color = theme.tintPrimary;
            const startDate = this.getStartingDayDateObj();
            const endDate = this.getEndingDayDateObj();
            const selectedDatesObj = getSelectedDatesObj(startDate, endDate, color);
            return selectedDatesObj;
        };
        this.getMonthTextStyle = () => {
            const { theme } = this.props;
            return {
                color: theme.darkPrimary,
                fontFamily: R.typography.weightRegular.fontFamily,
                fontWeight: R.typography.weightBold.fontWeight,
                fontSize: R.typography.fontSize.medium,
                margin: 0,
            };
        };
        this.getHolidayStatus = date => {
            const dateObj = new Date(date);
            return Object.keys(this.convertedHolidayObj).includes(date) || dateObj.getDay() === 0;
        };
        this.getMonthHolidayObj = (year, month) => {
            if (!this.props.holidayObj[year])
                return;
            return this.props.holidayObj[year][month];
        };
        this.getCalendarContainerStyle = () => {
            const { theme } = this.props;
            return {
                paddingHorizontal: R.spacing.xxs,
                borderColor: theme.lightSecondary,
                borderTopWidth: R.border.width.thin,
            };
        };
        this.renderHolidayList = date => {
            if (Object.keys(this.props.holidayObj).length === 0)
                return;
            const year = getFormattedDate(date, 'YYYY');
            const month = getFormattedDate(date, 'M');
            const monthHolidayObj = this.getMonthHolidayObj(year, month);
            if (!monthHolidayObj)
                return;
            return <DRNHolidayList holidayList={monthHolidayObj}/>;
        };
        this.renderDateComponent = ({ date, state, marking }) => {
            const { theme, calendarWidth } = this.props;
            return (<DRNDateCell accessibilityLabel="label_day" date={date.dateString} marking={marking} onDayPress={this.handleDatePress} style={{ backgroundColor: theme.lightPrimary }} isHoliday={this.getHolidayStatus(date.dateString)} calendarWidth={calendarWidth} disabled={state === 'disabled' || marking.disabled}/>);
        };
        this.renderCalendarList = () => {
            const _a = this.props, { theme, firstDay, pastScrollRange, futureScrollRange, onDayPress, disabledDays } = _a, rest = __rest(_a, ["theme", "firstDay", "pastScrollRange", "futureScrollRange", "onDayPress", "disabledDays"]);
            const markedDates = merge(this.getMarkedDatesObj(), rest.markedDates);
            return (<CalendarList key={theme.lightPrimary} {...rest} firstDay={firstDay} markingType={'period'} markedDates={markedDates} theme={Object.assign({}, customCalendarStyle, { 'stylesheet.calendar.header': Object.assign({}, customCalendarStyle['stylesheet.calendar.header'], { monthText: this.getMonthTextStyle() }), 'stylesheet.calendar.main': Object.assign({}, customCalendarStyle['stylesheet.calendar.main'], { container: this.getCalendarContainerStyle() }), calendarBackground: theme.lightPrimary })} pastScrollRange={pastScrollRange} futureScrollRange={futureScrollRange} dayComponent={this.renderDateComponent} calendarHeight={HEADER_HEIGHT + 6 * TOTAL_HEIGHT + 30} renderFooter={this.renderHolidayList} disabledDays={disabledDays}/>);
        };
        Object.keys(locales).forEach(locale => {
            //eslint-disable-next-line
            LocaleConfig.locales[locale] = locales[locale];
        });
        LocaleConfig.defaultLocale = props.locale;
        this.convertedHolidayObj = getConvertedHolidayObj(props.holidayObj) || {};
    }
    componentDidUpdate(prevProps, prevState) {
        const { startingDay, endingDay } = this.state;
        if (this.isRangeSelected() && (startingDay !== prevState.startingDay || endingDay !== prevState.endingDay)) {
            this.props.onRangeSelected && this.props.onRangeSelected(this.getRangeObj());
        }
    }
    componentWillUnmount() {
        const { onClose } = this.props;
        onClose && onClose(this.getRangeObj());
    }
    render() {
        // If the calendar is horizontal, we need to wrap this with ScrollView to cater small Android screens
        // https://github.com/traveloka/district/pull/810
        if (this.props.horizontal) {
            return (<ScrollView nestedScrollEnabled contentContainerStyle={this.getContainerStyle()}>
          {this.renderCalendarList()}
        </ScrollView>);
        }
        // Otherwise, simply wrap the CalendarList with a View
        return <View style={this.getContainerStyle()}>{this.renderCalendarList()}</View>;
    }
}
Calendar.propTypes = {
    onRangeSelected: PropTypes.func,
    onClose: PropTypes.func,
    firstDay: PropTypes.number,
    pastScrollRange: PropTypes.number,
    futureScrollRange: PropTypes.number,
    locale: PropTypes.string,
    theme: PropTypes.object,
    holidayObj: PropTypes.object,
    variant: PropTypes.oneOf(Object.values(VARIANTS)),
    calendarWidth: PropTypes.number,
    disabledDays: PropTypes.arrayOf(PropTypes.number),
    horizontal: PropTypes.bool,
};
Calendar.defaultProps = {
    pastScrollRange: 12,
    futureScrollRange: 12,
    firstDay: 1,
    locale: 'en',
    variant: VARIANTS.PERIOD_SELECT,
    holidayObj: {},
    calendarWidth: DRNUtil.getScreenWidth(),
    disabledDays: [],
    horizontal: false,
};
const customCalendarStyle = {
    'stylesheet.calendar.header': {
        week: {
            marginTop: R.spacing.m,
            flexDirection: 'row',
            justifyContent: 'space-around',
        },
        header: {
            flexDirection: 'row',
            justifyContent: 'flex-start',
            paddingLeft: R.spacing.xs,
        },
        dayHeader: Object.assign({}, R.typography.uiTiny, { color: R.color.uiDarkSecondary }),
    },
    'stylesheet.calendar.main': {
        week: {
            margin: 0,
            flexDirection: 'row',
            justifyContent: 'space-around',
        },
    },
};
const DRNCalendar = withTheme(Calendar);
DRNCalendar.VARIANTS = VARIANTS;
DRNCalendar.CONST = DAY_CONST;
export default DRNCalendar;
